import PropTypes from 'prop-types';
import './Card.css';

/**
 * Card Component
 * 
 * A reusable card container with glass-morphism styling.
 * Supports different variants and hover effects.
 * 
 * @param {node} children - Card content
 * @param {string} title - Optional card title
 * @param {string} subtitle - Optional card subtitle
 * @param {node} header - Optional custom header content
 * @param {node} footer - Optional footer content
 * @param {string} variant - Card style: 'default', 'elevated', 'bordered'
 * @param {boolean} hoverable - Enable hover effect
 * @param {boolean} clickable - Make card clickable
 * @param {function} onClick - Click handler
 * @param {string} className - Additional CSS classes
 */
export default function Card({
  children,
  title,
  subtitle,
  header,
  footer,
  variant = 'default',
  hoverable = false,
  clickable = false,
  onClick,
  className = ''
}) {
  const cardClasses = [
    'card',
    `card-${variant}`,
    hoverable && 'card-hoverable',
    clickable && 'card-clickable',
    className
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  const handleKeyPress = (e) => {
    if (clickable && onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={cardClasses}
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {(header || title || subtitle) && (
        <div className="card-header">
          {header || (
            <>
              {title && <h3 className="card-title">{title}</h3>}
              {subtitle && <p className="card-subtitle">{subtitle}</p>}
            </>
          )}
        </div>
      )}
      
      <div className="card-body">
        {children}
      </div>
      
      {footer && (
        <div className="card-footer">
          {footer}
        </div>
      )}
    </div>
  );
}

Card.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  header: PropTypes.node,
  footer: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'elevated', 'bordered']),
  hoverable: PropTypes.bool,
  clickable: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string
};
